import React, { useState, useRef, useEffect, useCallback } from 'react';
import { GripVertical, Calendar, ChevronDown, ChevronUp, CircleDashed, Ban, CheckCircle, Trash2, ExternalLink, Clock, MapPin, Users, Video, Mail, Zap, User, FileText, Loader2, UserPlus, Paperclip, Bold, Italic, Underline, Strikethrough, List, ListOrdered, AlignLeft, AlignCenter, AlignRight, Check, X, Link, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { DoneModal, PartialModal, BlockedModal, ClosePartialModal } from './StatusModals';
import DatePickerWithStats from './DatePickerWithStats';
import { PersonPicker } from './PersonPicker';

const API = '/api';

const PRIORITY_META_LOCAL_REPLACED_BY_TASK_CONSTANTS = null; // see ../../lib/taskConstants

import { useWorkAreas } from '../../contexts/WorkAreaContext';
import { PRIORITY_META, EFFORT_OPTIONS } from '../../lib/taskConstants';
import { OrkaTaskHeader } from '../orka/task/OrkaTaskHeader';
import { OrkaTaskFieldGroups } from '../orka/task/OrkaTaskFieldGroups';
import { adaptTaskToHeaderProps } from '../../design-system/taskCardAdapter';
import {
  PriorityPopoverPill, StatePopoverPill, IntentPopoverPill,
  TimePopoverPill, EffortPopoverPill, TitlePopoverPill, WorkAreaPopoverPill,
} from '../orka/task/TaskHeaderPopoverPills';

// Fallback color map for when context isn't loaded yet
const FALLBACK_COLORS = { '': '#E5E7EB' };

// Epic 7 — small inline sync-health indicator for calendar-linked tasks.
// green=synced, red=conflict, amber=pending/orphaned, slate=cancelled.
const SYNC_DOT_COLORS = {
  synced: '#16A34A',
  conflict: '#DC2626',
  pending: '#F59E0B',
  orphaned: '#F59E0B',
  cancelled: '#94A3B8',
  linked: '#3B82F6',
};

const SyncHealthDot = ({ task }) => {
  const linked = task.calendar_linked || task.calendar_event_id || task.is_calendar_import;
  if (!linked) return null;
  const hasConflict = !!task.sync_conflict || (Array.isArray(task.sync_conflict_fields) && task.sync_conflict_fields.length > 0);
  const status = hasConflict ? 'conflict' : (task.calendar_sync_status || 'linked');
  const color = SYNC_DOT_COLORS[status] || SYNC_DOT_COLORS.linked;
  const label = hasConflict
    ? `Calendar sync conflict: ${(task.sync_conflict_fields || []).join(', ')}`
    : `Calendar: ${status}`;
  return (
    <span
      data-testid="sync-health-dot"
      title={label}
      aria-label={label}
      className="inline-block w-2 h-2 rounded-full mr-1.5 flex-shrink-0"
      style={{ backgroundColor: color }}
    />
  );
};

const STATUS_META = {
  'To Do':       { bg: 'var(--orka-state-open-soft)',        text: 'var(--orka-state-open-text)',        icon: CircleDashed },
  'In Progress': { bg: 'var(--orka-state-in-progress-soft)', text: 'var(--orka-state-in-progress-text)', icon: Loader2 },
  'Partial':     { bg: 'var(--orka-state-partial-soft)',     text: 'var(--orka-state-partial-text)',     icon: Loader2 },
  'Blocked':     { bg: 'var(--orka-state-blocked-soft)',     text: 'var(--orka-state-blocked-text)',     icon: Ban },
  'Done':        { bg: 'var(--orka-state-done-soft)',        text: 'var(--orka-state-done-text)',        icon: CheckCircle },
};

const EFFORT_OPTIONS_LOCAL_REPLACED_BY_TASK_CONSTANTS = null; // see ../../lib/taskConstants

const getTextColorForBg = (bgColor) => {
  if (!bgColor) return '#000000';
  const hex = bgColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 128 ? '#000000' : '#FFFFFF';
};

const formatDuration = (start, end) => {
  if (!start || !end) return '';
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  const diff = (eh * 60 + em) - (sh * 60 + sm);
  if (diff <= 0) return '';
  const h = Math.floor(diff / 60);
  const m = diff % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

// Expandable Rich Text Editor (hidden formatting toolbar by default)
const ExpandableRichTextEditor = ({ value, onChange, onBlur, placeholder, onCancel, onSave, showSaveCancel }) => {
  const [showRaw, setShowRaw] = useState(false);
  const [showToolbar, setShowToolbar] = useState(false);
  const [localValue, setLocalValue] = useState(value || '');
  const editorRef = useRef(null);
  const initializedRef = useRef(false);
  
  // Set initial content via ref (NOT dangerouslySetInnerHTML) to avoid cursor issues
  useEffect(() => {
    if (editorRef.current && !initializedRef.current) {
      editorRef.current.innerHTML = value || '';
      initializedRef.current = true;
    }
  }, [value]);
  
  // Sync external value changes (e.g., cancel/reset)
  useEffect(() => {
    setLocalValue(value || '');
    if (editorRef.current && initializedRef.current && editorRef.current.innerHTML !== (value || '')) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  const execCommand = (cmd, val = null) => {
    document.execCommand(cmd, false, val);
    if (editorRef.current) {
      const html = editorRef.current.innerHTML;
      setLocalValue(html);
      onChange?.(html);
    }
  };

  const handleEditorInput = () => {
    if (editorRef.current) {
      const html = editorRef.current.innerHTML;
      setLocalValue(html);
      onChange?.(html);
    }
  };

  const handleEditorKeyDown = (e) => {
    if (e.key === 'Escape') { onCancel?.(); }
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { onSave?.(); }
  };

  return (
    <div className="border border orka-border-subtle rounded-lg overflow-hidden orka-surface-raised">
      {/* Expandable toolbar */}
      <div className="flex items-center gap-1 p-1 orka-surface-base border-b border orka-border-subtle">
        <button 
          onClick={() => setShowToolbar(!showToolbar)} 
          className={`px-2 py-1 text-[10px] rounded ${showToolbar ? 'bg-slate-700 text-white' : 'orka-surface-subtle text-stone-600 dark:text-slate-300 hover:bg-stone-300 dark:hover:bg-slate-600'}`}
        >
          {showToolbar ? 'Hide Toolbar' : 'Format'}
        </button>
        {showToolbar && (
          <>
            <div className="w-px h-4 bg-stone-300 slate-600 mx-1" />
            <button onClick={() => execCommand('bold')} className="p-1 hover:bg-stone-200 dark:hover:bg-slate-700 rounded" title="Bold"><Bold className="w-3 h-3" /></button>
            <button onClick={() => execCommand('italic')} className="p-1 hover:bg-stone-200 dark:hover:bg-slate-700 rounded" title="Italic"><Italic className="w-3 h-3" /></button>
            <button onClick={() => execCommand('underline')} className="p-1 hover:bg-stone-200 dark:hover:bg-slate-700 rounded" title="Underline"><Underline className="w-3 h-3" /></button>
            <button onClick={() => execCommand('strikeThrough')} className="p-1 hover:bg-stone-200 dark:hover:bg-slate-700 rounded" title="Strikethrough"><Strikethrough className="w-3 h-3" /></button>
            <div className="w-px h-4 bg-stone-300 slate-600 mx-1" />
            <button onClick={() => execCommand('insertUnorderedList')} className="p-1 hover:bg-stone-200 dark:hover:bg-slate-700 rounded" title="Bullet List"><List className="w-3 h-3" /></button>
            <button onClick={() => execCommand('insertOrderedList')} className="p-1 hover:bg-stone-200 dark:hover:bg-slate-700 rounded" title="Numbered List"><ListOrdered className="w-3 h-3" /></button>
            <div className="w-px h-4 bg-stone-300 slate-600 mx-1" />
            <button onClick={() => execCommand('justifyLeft')} className="p-1 hover:bg-stone-200 dark:hover:bg-slate-700 rounded"><AlignLeft className="w-3 h-3" /></button>
            <button onClick={() => execCommand('justifyCenter')} className="p-1 hover:bg-stone-200 dark:hover:bg-slate-700 rounded"><AlignCenter className="w-3 h-3" /></button>
            <button onClick={() => execCommand('justifyRight')} className="p-1 hover:bg-stone-200 dark:hover:bg-slate-700 rounded"><AlignRight className="w-3 h-3" /></button>
            <div className="w-px h-4 bg-stone-300 slate-600 mx-1" />
            <button onClick={() => setShowRaw(!showRaw)} className={`px-1.5 py-0.5 text-[9px] font-mono rounded ${showRaw ? 'bg-slate-800 text-green-400' : 'bg-stone-200 text-stone-600'}`}>&lt;/&gt;</button>
          </>
        )}
        <div className="flex-1" />
        {showSaveCancel && (
          <div className="flex gap-1">
            <button onClick={onCancel} className="p-1 hover:orka-bg-danger-soft rounded orka-text-danger" title="Cancel"><X className="w-4 h-4" /></button>
            <button onClick={onSave} className="p-1 hover:orka-bg-success-soft rounded orka-text-success" title="Save"><Check className="w-4 h-4" /></button>
          </div>
        )}
      </div>
      {showRaw ? (
        <textarea value={localValue} onChange={(e) => { setLocalValue(e.target.value); onChange?.(e.target.value); }} placeholder={placeholder} className="w-full p-2 min-h-[60px] font-mono text-xs bg-slate-900 text-green-400 resize-none focus:outline-none" />
      ) : (
        <div ref={editorRef} contentEditable onInput={handleEditorInput} onKeyDown={handleEditorKeyDown} onBlur={() => onBlur?.(localValue)} className="w-full p-2 min-h-[60px] text-sm focus:outline-none prose prose-sm max-w-none" data-placeholder={placeholder} style={{ direction: 'ltr', textAlign: 'left' }} />
      )}
    </div>
  );
};

// Quick Status Selector (always clickable, square button, follows card layering)
const QuickStatusSelector = ({ status, task, onUpdate, disabled = false, onOpenChange, onStatusWithModal }) => {
  const [isOpen, setIsOpen] = useState(false);
  const statusMeta = STATUS_META[status] || STATUS_META['To Do'];
  const StatusIcon = statusMeta.icon;

  const handleOpenChange = (open) => {
    setIsOpen(open);
    onOpenChange?.(open);
  };

  const handleStatusChange = (newStatus) => {
    handleOpenChange(false);
    
    // Statuses that require modals
    if (newStatus === 'Done' || newStatus === 'Partial' || newStatus === 'Blocked') {
      onStatusWithModal?.(newStatus);
      return;
    }
    
    // Instant update for To Do and In Progress
    onUpdate(newStatus);
    toast.success(`Status updated to ${newStatus}`);
  };

  const handleClick = (e) => {
    e.stopPropagation();
    if (disabled) return;
    
    // If task is already Partial, show the ClosePartial modal directly
    if (status === 'Partial') {
      onStatusWithModal?.('ClosePartial');
      return;
    }
    
    handleOpenChange(!isOpen);
  };

  return (
    <div className="relative">
      <button
        onClick={handleClick}
        className={`flex items-center justify-center w-[72px] h-[72px] rounded text-xs font-semibold transition-colors hover:opacity-90 ${disabled ? 'opacity-50' : 'cursor-pointer'}`}
        style={{ backgroundColor: statusMeta.bg, color: statusMeta.text }}
        title={status === 'Partial' ? 'Click to close this partial task' : `Status: ${status} - Click to change`}
        data-testid="quick-status-btn"
      >
        <StatusIcon className={`w-10 h-10 ${status === 'In Progress' || status === 'Partial' ? 'animate-spin' : ''}`} />
      </button>
      
      {isOpen && (
        <>
          <div className="fixed inset-0 z-[100]" onClick={(e) => { e.stopPropagation(); handleOpenChange(false); }} />
          <div className="absolute right-0 top-full mt-1 z-[101] orka-surface-raised rounded-lg shadow-xl border border orka-border-subtle py-1 min-w-[150px]">
            {Object.entries(STATUS_META).filter(([s]) => s !== 'Partial').map(([s, meta]) => {
              const Icon = meta.icon;
              return (
                <button
                  key={s}
                  onClick={(e) => { e.stopPropagation(); handleStatusChange(s); }}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-stone-50 dark:hover:bg-slate-800 dark:bg-slate-800 ${status === s ? 'orka-surface-subtle' : ''}`}
                  data-testid={`status-option-${s.toLowerCase().replace(' ', '-')}`}
                >
                  <Icon className="w-4 h-4" style={{ color: meta.text }} />
                  <span>{s}</span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

// Banner cell with inline editing
const EditableBannerCell = ({ value, onChange, onSave, onCancel, type = 'text', options = [], style = {}, editing, setEditing }) => {
  const [localValue, setLocalValue] = useState(value);
  
  useEffect(() => { setLocalValue(value); }, [value]);

  if (!editing) {
    return (
      <div 
        className="flex items-center px-2 h-full cursor-pointer hover:bg-black/10 transition-colors"
        style={style}
        onClick={(e) => { e.stopPropagation(); setEditing(true); }}
      >
        {type === 'time' ? (
          <span className="text-[11px] font-mono">{value || '--:--'}</span>
        ) : (
          <span className="text-xs font-medium truncate">{value}</span>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1 px-1 h-full orka-surface-raised" onClick={(e) => e.stopPropagation()}>
      {type === 'select' ? (
        <select 
          value={localValue} 
          onChange={(e) => setLocalValue(e.target.value)}
          className="text-xs p-1 border rounded orka-surface-raised w-full"
          autoFocus
        >
          {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        </select>
      ) : type === 'time' ? (
        <input 
          type="time" 
          value={localValue || ''} 
          onChange={(e) => setLocalValue(e.target.value)}
          className="text-xs p-1 border rounded orka-surface-raised font-mono w-20"
          autoFocus
        />
      ) : (
        <input 
          type="text" 
          value={localValue || ''} 
          onChange={(e) => setLocalValue(e.target.value)}
          className="text-xs p-1 border rounded orka-surface-raised flex-1 min-w-0"
          autoFocus
        />
      )}
      <button onClick={() => { onSave(localValue); setEditing(false); }} className="p-0.5 hover:orka-bg-success-soft rounded orka-text-success"><Check className="w-3.5 h-3.5" /></button>
      <button onClick={() => { setLocalValue(value); setEditing(false); onCancel?.(); }} className="p-0.5 hover:orka-bg-danger-soft rounded orka-text-danger"><X className="w-3.5 h-3.5" /></button>
    </div>
  );
};

const GRIP_WIDTH = 24;

const TaskCard = ({ task, context = 'timeline', fixedHeight, onUpdate, onDelete, isDragging = false, onDragStart, onDragEnd, hasUnsavedChanges = false, stackIndex = 0, onActiveChange, onCreateTask }) => {
  const { workAreaNames, workAreaColors, workAreaName } = useWorkAreas();
  const WORK_AREA_COLORS = { ...FALLBACK_COLORS, ...workAreaColors };
  const [isExpanded, setIsExpanded] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [hasEdits, setHasEdits] = useState(false);
  const [pendingChanges, setPendingChanges] = useState({});
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const cardRef = useRef(null);
  
  // Status modal states
  const [showDoneModal, setShowDoneModal] = useState(false);
  const [showPartialModal, setShowPartialModal] = useState(false);
  const [showBlockedModal, setShowBlockedModal] = useState(false);
  const [showClosePartialModal, setShowClosePartialModal] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);

  const priority = String(task.task_priority ?? 3);
  const priorityMeta = PRIORITY_META[priority] || PRIORITY_META["3"];
  const workAreaBg = WORK_AREA_COLORS[task.work_area_id] || WORK_AREA_COLORS['General'];
  const workAreaText = getTextColorForBg(workAreaBg);
  const statusMeta = STATUS_META[task.task_status] || STATUS_META['To Do'];
  const isMeeting = task.task_is_meeting || task.is_calendar_import;
  const stackIndent = stackIndex * GRIP_WIDTH;
  
  // Card is "active" when expanded, status dropdown is open, or banner is being edited
  const isActive = isExpanded || isStatusOpen || editingField !== null;
  
  // Notify parent when active state changes
  useEffect(() => {
    onActiveChange?.(isActive);
  }, [isActive, onActiveChange]);

  const handleQuickStatusUpdate = async (newStatus) => {
    await onUpdate(task.task_id, { status: newStatus });
  };

  // Handle status changes that require modals
  const handleStatusWithModal = (status) => {
    if (status === 'Done') setShowDoneModal(true);
    else if (status === 'Partial') setShowPartialModal(true);
    else if (status === 'Blocked') setShowBlockedModal(true);
    else if (status === 'ClosePartial') setShowClosePartialModal(true);
  };

  // Handle Done modal confirm
  const handleDoneConfirm = async (updates) => {
    setSavingStatus(true);
    try {
      // Extract followup task data if present
      const { followup, ...taskUpdates } = updates;
      
      // Update the task status
      await onUpdate(task.task_id, taskUpdates);
      
      // Create followup task if specified
      if (followup && followup.task_title) {
        const API = '';
        await fetch(`${API}/api/tasks`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(followup)
        });
        toast.success('Task completed & followup created!');
      } else {
        toast.success('Task marked as Done');
      }
      
      setShowDoneModal(false);
    } catch {
      toast.error('Failed to update task');
    } finally {
      setSavingStatus(false);
    }
  };

  // Handle Partial modal confirm
  const handlePartialConfirm = async (partialData) => {
    setSavingStatus(true);
    try {
      // Update current task with partial status
      const { continuation, ...taskUpdates } = partialData;
      taskUpdates.task_status = 'Partial';
      
      // Create continuation task via API to get its ID
      let continuationTaskId = null;
      if (continuation && continuation.task_title) {
        const contResponse = await fetch(`${API}/tasks`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(continuation)
        });
        if (contResponse.ok) {
          const contTask = await contResponse.json();
          continuationTaskId = contTask.task_id;
        }
      }
      
      // Link the original to the continuation
      if (continuationTaskId) {
        taskUpdates.continuation_task_id = continuationTaskId;
      }
      
      await onUpdate(task.task_id, taskUpdates);
      
      setShowPartialModal(false);
      toast.success('Task and continuation saved');
    } catch {
      toast.error('Failed to update partial progress');
    } finally {
      setSavingStatus(false);
    }
  };

  // Handle Blocked modal confirm
  const handleBlockedConfirm = async (updates) => {
    setSavingStatus(true);
    try {
      await onUpdate(task.task_id, updates);
      setShowBlockedModal(false);
      toast.success('Task marked as Blocked');
    } catch {
      toast.error('Failed to update task');
    } finally {
      setSavingStatus(false);
    }
  };

  // Handle ClosePartial confirm (mark as Done without effort)
  const handleClosePartialConfirm = async (updates) => {
    setSavingStatus(true);
    try {
      await onUpdate(task.task_id, updates);
      setShowClosePartialModal(false);
      toast.success('Partial task closed');
    } catch {
      toast.error('Failed to close task');
    } finally {
      setSavingStatus(false);
    }
  };

  const handleBannerSave = async (field, value) => {
    setEditingField(null);
    if (value !== task[field]) {
      await onUpdate(task.task_id, { [field]: value });
    }
  };

  // For backlog context
  if (context === 'backlog') {
    return (
      <div ref={cardRef} draggable={!task.task_hidden} onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; onDragStart?.(task); }} onDragEnd={onDragEnd}
        className={`flex items-center gap-2 px-2 py-2 rounded-lg border orka-surface-raised hover:shadow-sm cursor-grab ${task._isSupporting ? 'border-l-[3px] border-l-orange-400 border-t-stone-200 border-r-stone-200 border-b-stone-200' : task._isObserved ? 'border-l-[3px] border-l-teal-400 border-t-stone-200 border-r-stone-200 border-b-stone-200' : 'border orka-border-subtle'} ${isDragging ? 'opacity-50' : ''} ${hasUnsavedChanges ? 'ring-2 ring-amber-400' : ''}`}
        style={{ marginLeft: stackIndent }}
        data-testid={`backlog-task-${task.task_id}`}>
        <GripVertical className="w-3 h-3 orka-text-tertiary" />
        <span className="w-6 h-6 rounded text-xs font-bold flex items-center justify-center" style={{ backgroundColor: priorityMeta.bg, color: priorityMeta.text }}>{priorityMeta.label}</span>
        <span className="px-2 py-0.5 rounded-full text-[10px] font-medium truncate max-w-[80px]" style={{ backgroundColor: workAreaBg, color: workAreaText }}>{workAreaName(task.work_area_id) || 'General'}</span>
        <span className="text-sm truncate flex-1 orka-text-secondary">{task.task_title}</span>
        <QuickStatusSelector status={task.task_status} task={task} onUpdate={handleQuickStatusUpdate} onStatusWithModal={handleStatusWithModal} />
        <span className="text-xs font-bold font-mono w-6 text-center orka-text-secondary">{task.task_estimated_effort || 1}</span>
        {task.task_reschedule_count > 0 && (
          <span className="text-[10px] font-medium orka-text-warning orka-bg-warning-soft px-1.5 py-0.5 rounded-full" title={`Rescheduled ${task.task_reschedule_count} time${task.task_reschedule_count > 1 ? 's' : ''}`}>
            R{task.task_reschedule_count}
          </span>
        )}
        
        {/* Status Modals for backlog context */}
        <DoneModal open={showDoneModal} onOpenChange={setShowDoneModal} task={task} onConfirm={handleDoneConfirm} saving={savingStatus} />
        <PartialModal open={showPartialModal} onOpenChange={setShowPartialModal} task={task} onConfirm={handlePartialConfirm} saving={savingStatus} />
        <BlockedModal open={showBlockedModal} onOpenChange={setShowBlockedModal} task={task} onConfirm={handleBlockedConfirm} saving={savingStatus} />
        <ClosePartialModal open={showClosePartialModal} onOpenChange={setShowClosePartialModal} task={task} onConfirm={handleClosePartialConfirm} saving={savingStatus} />
      </div>
    );
  }

  const cardHeight = fixedHeight || (EFFORT_OPTIONS.find(e => e.value === task.task_estimated_effort)?.minutes || 30) / 15 * 30;

  // Active cards need much higher z-index to appear above ALL other cards
  // For multiple active cards, smaller duration goes on top
  const getActiveZIndex = () => {
    if (!isActive) return undefined;
    // Base of 200 for active, plus inverse of duration so smaller cards are higher
    const durationSlots = task.task_local_end_time && task.task_local_start_time 
      ? Math.ceil((
          (parseInt(task.task_local_end_time.split(':')[0]) * 60 + parseInt(task.task_local_end_time.split(':')[1])) -
          (parseInt(task.task_local_start_time.split(':')[0]) * 60 + parseInt(task.task_local_start_time.split(':')[1]))
        ) / 15)
      : 4;
    return 200 + (20 - Math.min(durationSlots, 20)); // Smaller duration = higher z-index
  };

  return (
    <div 
      ref={cardRef} 
      className={`rounded-lg overflow-hidden shadow-sm border orka-surface-raised ${task._isSupporting ? 'border-l-[3px] border-l-orange-400 border-t-stone-200 border-r-stone-200 border-b-stone-200' : task._isObserved ? 'border-l-[3px] border-l-teal-400 border-t-stone-200 border-r-stone-200 border-b-stone-200' : 'border orka-border-subtle'} ${isDragging ? 'opacity-50' : ''} ${hasUnsavedChanges || hasEdits ? 'ring-2 ring-amber-400' : ''} ${isActive ? 'relative' : ''}`}
      style={{ 
        height: (isExpanded || isStatusOpen) ? 'auto' : cardHeight,
        minHeight: 36,
        zIndex: getActiveZIndex()
      }} 
      data-testid={`task-card-${task.task_id}`}
    >
      {/* V3 Full-Bleed Task Identity Header — single source of truth for
          the header strip. Replaces the legacy 7-cell inline-edit banner
          which caused double-rendering with the interior header. Drag is
          routed through OrkaTaskHeader's move handle; inline editing now
          lives in the popover pills inside the expanded body. */}
      <div
        className={`relative ${isExpanded ? 'sticky top-0 z-10' : ''}`}
        data-testid={`task-v3-header-strip-${task.task_id}`}
      >
        <OrkaTaskHeader
          {...adaptTaskToHeaderProps(task, { variant: 'board' })}
          onDragStart={!task.task_hidden && !isExpanded ? (e) => { e.dataTransfer.effectAllowed = 'move'; onDragStart?.(task); } : undefined}
          onDragEnd={onDragEnd}
        />
      </div>

      {/* Footer strip — expand chevron lives HERE, never overlaid on the
          header (prevents collisions with the move handle, more menu, or
          status pills). */}
      <div className="flex items-center justify-end gap-2 px-3 py-1.5 border-t orka-border-subtle orka-surface-raised">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setIsExpanded(!isExpanded); }}
          className="h-6 w-6 flex items-center justify-center rounded-full border orka-border-subtle orka-surface-base hover:orka-surface-subtle orka-transition-colors"
          aria-label={isExpanded ? 'Collapse task card' : 'Expand task card'}
          data-testid={`task-card-expand-${task.task_id}`}
        >
          {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
      </div>

      {/* 2px gap */}
      <div className="h-[2px] orka-surface-subtle" />

      {/* Card Body - Always visible, greyed when collapsed except Status button */}
      <div className="orka-surface-raised relative">
        <CardBody 
          task={task} 
          onUpdate={onUpdate} 
          onDelete={onDelete}
          isExpanded={isExpanded}
          isStatusOpen={isStatusOpen}
          priorityMeta={priorityMeta}
          workAreaBg={workAreaBg}
          workAreaText={workAreaText}
          statusMeta={statusMeta}
          handleQuickStatusUpdate={handleQuickStatusUpdate}
          setHasEdits={setHasEdits}
          onStatusOpenChange={setIsStatusOpen}
          onStatusWithModal={handleStatusWithModal}
        />
      </div>
      
      {/* Status Modals */}
      <DoneModal open={showDoneModal} onOpenChange={setShowDoneModal} task={task} onConfirm={handleDoneConfirm} saving={savingStatus} />
      <PartialModal open={showPartialModal} onOpenChange={setShowPartialModal} task={task} onConfirm={handlePartialConfirm} saving={savingStatus} />
      <BlockedModal open={showBlockedModal} onOpenChange={setShowBlockedModal} task={task} onConfirm={handleBlockedConfirm} saving={savingStatus} />
      <ClosePartialModal open={showClosePartialModal} onOpenChange={setShowClosePartialModal} task={task} onConfirm={handleClosePartialConfirm} saving={savingStatus} />
    </div>
  );
};

// ===== LEGACY_BANNER_DELETED =====
// (The former 7-cell inline-edit banner has been removed. Inline editing
// for title / priority / time / effort / work-area / state / intent now
// lives in the popover pills inside CardBody. See OrkaTaskHeader for the
// canonical V3 full-bleed identity strip.)
// Body content - always rendered, greyed when collapsed except status button
const CardBody = ({ task, onUpdate, onDelete, isExpanded, isStatusOpen, priorityMeta, workAreaBg, workAreaText, statusMeta, handleQuickStatusUpdate, setHasEdits, onStatusOpenChange, onStatusWithModal }) => {
  const { workAreaNames, workAreaColors, workAreaName } = useWorkAreas();
  const WORK_AREA_COLORS = { ...FALLBACK_COLORS, ...workAreaColors };
  const [localDesc, setLocalDesc] = useState(task.task_description || '');
  const [localNextSteps, setLocalNextSteps] = useState(task.next_steps || '');
  const [localImpact, setLocalImpact] = useState(task.impact || '');
  const [localRelatedEpic, setLocalRelatedEpic] = useState(task.big_picture_id || '');
  const [localRelatedLinks, setLocalRelatedLinks] = useState(task.task_related_links || '');
  const [localResponsiblePersonId, setLocalResponsiblePersonId] = useState(task.responsible_person_id || '');
  const [localResponsiblePersonName, setLocalResponsiblePersonName] = useState(task.responsible_person_name || '');
  const [localMeetingLocation, setLocalMeetingLocation] = useState(task.meeting_location || '');
  const [saving, setSaving] = useState(false);
  const [newInviteEmail, setNewInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [editingInterior, setEditingInterior] = useState(false);
  const [interiorChanges, setInteriorChanges] = useState({});
  const [bigPictureItems, setBigPictureItems] = useState([]);
  const [orgMembers, setOrgMembers] = useState([]);

  // Fetch Big Picture items for the Epic/Project/Goal dropdown
  useEffect(() => {
    if (!isExpanded) return;
    fetch(`${API}/bigpicture/items`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(items => setBigPictureItems((items || []).filter(i => ['GOAL', 'OKR', 'PROJECT', 'MILESTONE', 'EPIC', 'SPRINT'].includes(i.type))))
      .catch(() => {});
  }, [isExpanded]);

  // Phase 5C: Fetch org members for PersonPicker (Responsible field)
  useEffect(() => {
    if (!isExpanded) return;
    fetch(`${API}/org/members`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(members => setOrgMembers(members || []))
      .catch(() => {});
  }, [isExpanded]);

  // Card is visually expanded when either expanded or status is open
  const isVisuallyExpanded = isExpanded || isStatusOpen;

  const StatusIcon = statusMeta.icon;
  const isMeeting = task.task_is_meeting || task.is_calendar_import;
  const attendees = task.meeting_attendees || [];
  const attachments = task.meeting_attachments || [];

  // Sync to Google Calendar when meeting changes
  const syncMeetingToCalendar = async (updates) => {
    if (!task.calendar_event_id) return;
    
    try {
      // The backend /api/tasks/{task_id} update already syncs meeting changes to Google Calendar
      // via the existing logic - but let's call a dedicated endpoint if we need explicit sync
      toast.info('Syncing changes to Google Calendar...');
    } catch (err) {
      console.error('Calendar sync error:', err);
    }
  };

  const handleInteriorChange = (field, value) => {
    setEditingInterior(true);
    setInteriorChanges(prev => ({ ...prev, [field]: value }));
    setHasEdits(true);
  };

  const handleSaveAll = async () => {
    setSaving(true);
    const updates = { ...interiorChanges };
    
    // Include local text field changes
    if (localDesc !== task.task_description) updates.description = localDesc;
    if (localNextSteps !== task.next_steps) updates.next_steps = localNextSteps;
    if (localImpact !== task.impact) updates.impact = localImpact;
    if (localRelatedEpic !== task.big_picture_id) updates.big_picture_id = localRelatedEpic;
    if (localRelatedLinks !== task.task_related_links) updates.task_related_links = localRelatedLinks;
    // Phase 5C: Send canonical responsible_person_id + responsible_person_name
    if (localResponsiblePersonId !== (task.responsible_person_id || '')) {
      updates.responsible_person_id = localResponsiblePersonId || '';
      updates.responsible_person_name = localResponsiblePersonName || '';
      if (!localResponsiblePersonId) {
        updates.task_supporter_membership_id = null;
      }
    }
    if (localMeetingLocation !== task.meeting_location) updates.meeting_location = localMeetingLocation;

    if (Object.keys(updates).length > 0) {
      // Call onUpdate - this will update the task AND trigger calendar sync on backend
      // The backend sync_task_to_calendar handles pushing time/duration changes to Google Calendar
      await onUpdate(task.task_id, updates);
      
      // Show calendar sync message for meeting time changes
      const hasTimeChanges = updates.task_start_time !== undefined || updates.task_end_time !== undefined || updates.task_estimated_effort !== undefined;
      if (isMeeting && hasTimeChanges && task.calendar_event_id) {
        toast.info('Meeting changes synced to Google Calendar - attendees will be notified');
      }
    }
    
    setEditingInterior(false);
    setInteriorChanges({});
    setHasEdits(false);
    setSaving(false);
    toast.success('Changes saved');
  };

  const handleFieldKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSaveAll(); }
  };

  const handleCancelAll = () => {
    // Reset all local values
    setLocalDesc(task.task_description || '');
    setLocalNextSteps(task.next_steps || '');
    setLocalImpact(task.impact || '');
    setLocalRelatedEpic(task.big_picture_id || '');
    setLocalRelatedLinks(task.task_related_links || '');
    setLocalResponsiblePersonId(task.responsible_person_id || '');
    setLocalResponsiblePersonName(task.responsible_person_name || '');
    setLocalMeetingLocation(task.meeting_location || '');
    setEditingInterior(false);
    setInteriorChanges({});
    setHasEdits(false);
  };

  const sendInvite = async () => {
    if (!newInviteEmail || !newInviteEmail.includes('@')) { toast.error('Valid email required'); return; }
    setInviting(true);
    try {
      const res = await fetch(`${API}/tasks/${task.task_id}/invite`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ email: newInviteEmail }) });
      if (res.ok) { toast.success(`Invitation sent to ${newInviteEmail}`); setNewInviteEmail(''); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch { toast.error('Failed to send'); }
    finally { setInviting(false); }
  };

  const getResponseColor = (s) => s === 'accepted' ? 'bg-emerald-100 text-emerald-700' : s === 'tentative' ? 'bg-amber-100 text-amber-700' : s === 'declined' ? 'bg-red-100 text-red-700' : 'orka-surface-subtle text-stone-600';
  const getResponseLabel = (s) => s === 'accepted' ? 'Accepted' : s === 'tentative' ? 'Maybe' : s === 'declined' ? 'Declined' : 'Pending';

  return (
    <div className={`relative ${isVisuallyExpanded ? 'max-h-[70vh] overflow-y-auto' : 'max-h-[280px] overflow-hidden'}`}>
      {/* Blocked Banner - Show when task is blocked */}
      {task.task_status === 'Blocked' && (
        <div className="orka-bg-danger text-white px-4 py-2 flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          <span className="text-sm font-medium truncate">
            {task.blocked_reason || 'Task is blocked - add reason below'}
          </span>
        </div>
      )}

      {/* V3 Full-Bleed Identity Header is now rendered ONCE at the top of
          TaskCard's outer shell (see `task-v3-header-strip-*`). Only the
          inline popover pill rail (Title/Priority/State/Intent/Time/Effort/
          WorkArea) remains here — used for quick edits inside the expanded
          body. */}
      <div
        className={`px-4 pt-3 pb-1 orka-transition-colors ${isVisuallyExpanded ? '' : 'opacity-40 grayscale-[40%] pointer-events-none'}`}
        data-testid={`task-v3-pills-${task.task_id}`}
      >
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          <TitlePopoverPill task={task} onSave={(v) => onUpdate(task.task_id, { task_title: v })}>
            <span className="text-[10px] font-medium px-2 py-0.5 rounded-full orka-bg-intent-soft orka-text-intent">Edit title</span>
          </TitlePopoverPill>
          <PriorityPopoverPill task={task} onSave={(v) => onUpdate(task.task_id, { task_priority: v })}>
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
              style={{ backgroundColor: PRIORITY_META[String(task.task_priority ?? 3)]?.bg, color: PRIORITY_META[String(task.task_priority ?? 3)]?.text }}>
              {PRIORITY_META[String(task.task_priority ?? 3)]?.label || 'P3'}
            </span>
          </PriorityPopoverPill>
          <StatePopoverPill task={task} onSave={(legacy) => onUpdate(task.task_id, { task_status: legacy })}>
            <span className="text-[10px] font-medium px-2 py-0.5 rounded-full orka-bg-info-soft orka-text-info">
              {task.task_status || 'To Do'}
            </span>
          </StatePopoverPill>
          <IntentPopoverPill task={task} onSave={(k) => onUpdate(task.task_id, { task_intent: k })}>
            <span className="text-[10px] font-medium px-2 py-0.5 rounded-full orka-bg-intent-soft orka-text-intent">
              {task.task_intent || task.intent || 'plan'}
            </span>
          </IntentPopoverPill>
          <TimePopoverPill task={task} onSave={(updates) => onUpdate(task.task_id, updates)}>
            <span className="text-[10px] font-mono px-2 py-0.5 rounded-full orka-surface-subtle orka-text-secondary border orka-border-subtle">
              {task.task_local_start_time || '--:--'} - {task.task_local_end_time || '--:--'}
            </span>
          </TimePopoverPill>
          <EffortPopoverPill task={task} onSave={(v) => onUpdate(task.task_id, { task_estimated_effort: v })}>
            <span className="text-[10px] font-mono px-2 py-0.5 rounded-full orka-surface-subtle orka-text-secondary border orka-border-subtle">
              E{task.task_estimated_effort || 1}
            </span>
          </EffortPopoverPill>
          <WorkAreaPopoverPill task={task} options={workAreaNames} onSave={(v) => onUpdate(task.task_id, { work_area_id: v })}>
            <span className="text-[10px] font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: workAreaBg, color: workAreaText }}>
              {workAreaName(task.work_area_id) || 'General'}
            </span>
          </WorkAreaPopoverPill>
        </div>
      </div>

      {/* Status Button - Top Right, part of interior, NOT greyed out, follows card layering */}
      <div className="absolute top-3 right-3" style={{ pointerEvents: 'auto' }}>
        <QuickStatusSelector 
          status={task.task_status} 
          task={task}
          onUpdate={handleQuickStatusUpdate} 
          onOpenChange={onStatusOpenChange}
          onStatusWithModal={onStatusWithModal}
        />
      </div>

      {/* Interior content - greyed when collapsed (but not when status is open) */}
      <div className={`orka-transition-colors ${isVisuallyExpanded ? '' : 'pointer-events-none'}`}>
        <div className={`p-4 space-y-3 ${isVisuallyExpanded ? 'opacity-100' : 'opacity-40 grayscale-[40%]'}`}>

          {/* V4 Canonical field-groups view — always rendered at the TOP of the
              open body. This is the new reconciled task editor. The legacy
              ad-hoc fields below remain available as a safety net while the
              rest of the card is migrated field-by-field. */}
          {isVisuallyExpanded && (
            <div data-testid={`task-field-groups-wrap-${task.task_id}`}>
              <OrkaTaskFieldGroups
                task={task}
                onChange={(key, value) => {
                  onUpdate?.(task.task_id, { [key]: value });
                  setHasEdits?.(true);
                }}
              />
              <div className="flex items-center gap-2 mt-4 mb-2 text-[10px] uppercase tracking-wider orka-text-tertiary font-bold">
                <span className="flex-1 h-px orka-bg-subtle" />
                Legacy fields (read-only reference)
                <span className="flex-1 h-px orka-bg-subtle" />
              </div>
            </div>
          )}

          {/* Title Row with "is meeting" toggle underneath */}
          <div className="pr-32">
            <h3 className="font-semibold orka-text-primary text-base">{task.task_title}</h3>
            <label className="flex items-center gap-2 mt-1 cursor-pointer">
              <Checkbox 
                checked={task.task_is_meeting || task.is_calendar_import} 
                onCheckedChange={(checked) => {
                  if (checked) {
                    // When marking as meeting: Set P0 and calculate effort from duration
                    const updates = { task_is_meeting: true, task_priority: 0 };
                    // Calculate effort from duration if times exist
                    if (task.task_local_start_time && task.task_local_end_time) {
                      const [sh, sm] = task.task_local_start_time.split(':').map(Number);
                      const [eh, em] = task.task_local_end_time.split(':').map(Number);
                      const duration = (eh * 60 + em) - (sh * 60 + sm);
                      let effort = 1;
                      if (duration <= 15) effort = 1;
                      else if (duration <= 30) effort = 2;
                      else if (duration <= 60) effort = 3;
                      else if (duration <= 180) effort = 5;
                      else if (duration <= 360) effort = 8;
                      else effort = 13;
                      updates.task_estimated_effort = effort;
                    }
                    handleInteriorChange('task_is_meeting', true);
                    handleInteriorChange('task_priority', 0);
                    if (updates.task_estimated_effort) handleInteriorChange('task_estimated_effort', updates.task_estimated_effort);
                  } else {
                    handleInteriorChange('task_is_meeting', false);
                  }
                }}
                className="h-4 w-4"
              />
              <span className="text-xs orka-text-info flex items-center gap-1">
                <Calendar className="w-3 h-3" />Is Meeting
              </span>
            </label>
          </div>

          {/* Description */}
          <div>
            <label className="text-xs font-medium orka-text-tertiary mb-1 flex items-center gap-1"><FileText className="w-3 h-3" /> Description</label>
            <ExpandableRichTextEditor 
              value={localDesc} 
              onChange={(v) => { setLocalDesc(v); handleInteriorChange('description', v); }} 
              onSave={handleSaveAll}
              placeholder="Description..." 
              showSaveCancel={false}
            />
          </div>

          {/* Row: Priority, Effort Score, Work Area */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-medium orka-text-tertiary mb-1 block">Priority</label>
              <Select value={String(task.task_priority ?? 3)} onValueChange={(v) => handleInteriorChange('task_priority', parseInt(v))}>
                <SelectTrigger className="orka-surface-raised h-9" style={{ backgroundColor: priorityMeta.bg, color: priorityMeta.text }}><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(PRIORITY_META).map(([v, m]) => <SelectItem key={v} value={v}><span className="px-2 py-0.5 rounded text-xs font-bold" style={{ backgroundColor: m.bg, color: m.text }}>{m.label}</span></SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium orka-text-tertiary mb-1 block">Est. Effort</label>
              <Select value={String(task.task_estimated_effort || 1)} onValueChange={(v) => handleInteriorChange('task_estimated_effort', parseInt(v))}>
                <SelectTrigger className="orka-surface-raised h-9"><SelectValue /></SelectTrigger>
                <SelectContent>{EFFORT_OPTIONS.map(o => <SelectItem key={o.value} value={String(o.value)}>{o.value} ({o.minutes}m)</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium orka-text-tertiary mb-1 block">Work Area</label>
              <Select value={task.work_area_id || 'General'} onValueChange={(v) => handleInteriorChange('work_area_id', v)}>
                <SelectTrigger className="orka-surface-raised h-9" style={{ backgroundColor: workAreaBg, color: workAreaText }}><SelectValue /></SelectTrigger>
                <SelectContent>{workAreaNames.map(a => <SelectItem key={a} value={a}><span className="px-2 py-0.5 rounded text-xs" style={{ backgroundColor: WORK_AREA_COLORS[a] || '#E5E7EB', color: getTextColorForBg(WORK_AREA_COLORS[a] || '#E5E7EB') }}>{a}</span></SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          {/* Row: Date, Duration, Start Time, End Time */}
          <div className="grid grid-cols-4 gap-3">
            <div>
              <label className="text-xs font-medium orka-text-tertiary mb-1 flex items-center gap-1"><Calendar className="w-3 h-3" /> Date</label>
              <div className="flex gap-1">
                <DatePickerWithStats
                  value={task.task_local_date || ''}
                  onChange={(val) => handleInteriorChange('task_delivery_date', val || null)}
                  triggerClassName="h-9 flex-1"
                />
                {task.task_local_date && (
                  <button
                    onClick={() => {
                      onUpdate(task.task_id, { task_delivery_date: null, task_start_time: null, task_end_time: null });
                    }}
                    className="h-9 px-1.5 text-xs text-red-400 hover:orka-text-danger hover:orka-bg-danger-soft rounded transition-colors"
                    title="Clear date (move to backlog)"
                    data-testid="clear-date-btn"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
            <div>
              <label className="text-xs font-medium orka-text-tertiary mb-1 block">Duration</label>
              <div className="h-9 px-3 flex items-center orka-surface-subtle rounded-md text-sm font-mono">{formatDuration(interiorChanges.start_time ?? task.task_local_start_time, interiorChanges.end_time ?? task.task_local_end_time) || '-'}</div>
            </div>
            <div>
              <label className="text-xs font-medium orka-text-tertiary mb-1 flex items-center gap-1"><Clock className="w-3 h-3" /> Start</label>
              <Input type="time" value={interiorChanges.start_time ?? task.task_local_start_time ?? ''} onChange={(e) => {
                const newStart = e.target.value;
                handleInteriorChange('start_time', newStart);
                // Auto-calc duration and effort for ALL tasks when time changes
                const currentEnd = interiorChanges.end_time ?? task.task_local_end_time;
                if (newStart && currentEnd) {
                  const [sh, sm] = newStart.split(':').map(Number);
                  const [eh, em] = currentEnd.split(':').map(Number);
                  const dur = (eh * 60 + em) - (sh * 60 + sm);
                  if (dur > 0) {
                    let eff = dur <= 15 ? 1 : dur <= 30 ? 2 : dur <= 60 ? 3 : dur <= 180 ? 5 : dur <= 360 ? 8 : 13;
                    handleInteriorChange('task_estimated_effort', eff);
                  }
                }
              }} className="orka-surface-raised font-mono text-sm h-9" data-testid="interior-start-time" />
            </div>
            <div>
              <label className="text-xs font-medium orka-text-tertiary mb-1 flex items-center gap-1"><Clock className="w-3 h-3" /> End</label>
              <Input type="time" value={interiorChanges.end_time ?? task.task_local_end_time ?? ''} onChange={(e) => {
                const newEnd = e.target.value;
                handleInteriorChange('end_time', newEnd);
                // Auto-calc duration and effort for ALL tasks when time changes
                const currentStart = interiorChanges.start_time ?? task.task_local_start_time;
                if (currentStart && newEnd) {
                  const [sh, sm] = currentStart.split(':').map(Number);
                  const [eh, em] = newEnd.split(':').map(Number);
                  const dur = (eh * 60 + em) - (sh * 60 + sm);
                  if (dur > 0) {
                    let eff = dur <= 15 ? 1 : dur <= 30 ? 2 : dur <= 60 ? 3 : dur <= 180 ? 5 : dur <= 360 ? 8 : 13;
                    handleInteriorChange('task_estimated_effort', eff);
                  }
                }
              }} className="orka-surface-raised font-mono text-sm h-9" data-testid="interior-end-time" />
            </div>
          </div>

          {/* Row: Impact, Next Steps (hidden formatting ribbon, expandable) */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium orka-text-tertiary mb-1 block">Impact</label>
              <ExpandableRichTextEditor 
                value={localImpact} 
                onChange={(v) => { setLocalImpact(v); handleInteriorChange('impact', v); }} 
                onSave={handleSaveAll}
                placeholder="Business impact..." 
                showSaveCancel={false}
              />
            </div>
            <div>
              <label className="text-xs font-medium orka-text-tertiary mb-1 block">Next Steps</label>
              <ExpandableRichTextEditor 
                value={localNextSteps} 
                onChange={(v) => { setLocalNextSteps(v); handleInteriorChange('next_steps', v); }} 
                onSave={handleSaveAll}
                placeholder="Action items..." 
                showSaveCancel={false}
              />
            </div>
          </div>

          {/* Row: Related Epic/Project/Goal, Responsible Person */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium orka-text-tertiary mb-1 flex items-center gap-1"><Zap className="w-3 h-3" /> Epic/Project/Goal</label>
              <Select value={localRelatedEpic || '_none'} onValueChange={(v) => { const val = v === '_none' ? '' : v; setLocalRelatedEpic(val); handleInteriorChange('big_picture_id', val); }}>
                <SelectTrigger className="orka-surface-raised h-9 text-sm" data-testid="task-related-epic-select">
                  <SelectValue placeholder="Link to Big Picture..." />
                </SelectTrigger>
                <SelectContent className="max-h-[250px]">
                  <SelectItem value="_none">None</SelectItem>
                  {bigPictureItems.map(bp => (
                    <SelectItem key={bp.item_id} value={bp.item_id} className="text-xs">
                      <span className="font-medium">{bp.type}</span>: {bp.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium orka-text-tertiary mb-1 flex items-center gap-1"><User className="w-3 h-3" /> Responsible</label>
              <PersonPicker
                value={localResponsiblePersonId}
                onChange={(userId, member) => {
                  setLocalResponsiblePersonId(userId || '');
                  setLocalResponsiblePersonName(member?.name || member?.email || '');
                  handleInteriorChange('responsible_person_id', userId || '');
                }}
                orgMembers={orgMembers}
                placeholder="Select person..."
                data-testid="task-responsible-picker"
              />
            </div>
          </div>

          {/* Related Links */}
          <div>
            <label className="text-xs font-medium orka-text-tertiary mb-1 flex items-center gap-1"><Link className="w-3 h-3" /> Related Links</label>
            <Input value={localRelatedLinks} onChange={(e) => { setLocalRelatedLinks(e.target.value); handleInteriorChange('task_related_links', e.target.value); }} placeholder="Links (comma separated)..." onKeyDown={handleFieldKeyDown} className="orka-surface-raised h-9" />
          </div>

          {/* Save/Cancel Buttons for interior changes */}
          {editingInterior && (
            <div className="flex justify-end gap-2 pt-2 border-t border orka-border-subtle">
              <Button variant="outline" size="sm" onClick={handleCancelAll}><X className="w-4 h-4 mr-1" />Cancel</Button>
              <Button size="sm" onClick={handleSaveAll} disabled={saving}>{saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}Save Changes</Button>
            </div>
          )}

          {/* 5px gap before Meeting Details */}
          {isMeeting && <div className="h-[5px]" />}

          {/* Meeting Details Section - No changes needed */}
          {isMeeting && (
            <div className="bg-gradient-to-br from-blue-100 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/20 rounded-xl p-4 space-y-4 border orka-border-subtle">
              <h3 className="font-semibold text-blue-800 flex items-center gap-2"><Calendar className="w-5 h-5" />Meeting Details</h3>

              <div className="flex flex-wrap gap-2">
                {task.google_meet_link && <a href={task.google_meet_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2.5 orka-bg-success text-white rounded-lg text-sm font-medium hover:bg-emerald-700 shadow-sm"><Video className="w-4 h-4" />Join Google Meet</a>}
                {task.calendar_html_link && <a href={task.calendar_html_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2.5 orka-bg-info text-white rounded-lg text-sm font-medium hover:bg-blue-700 shadow-sm"><ExternalLink className="w-4 h-4" />View in Calendar</a>}
                {attendees.length > 0 && <a href={`mailto:${attendees.map(a => a.email).join(',')}`} className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-700 text-white rounded-lg text-sm font-medium hover:bg-slate-800 shadow-sm"><Mail className="w-4 h-4" />Email All ({attendees.length})</a>}
              </div>

              <div>
                <label className="text-xs font-medium orka-text-info mb-1 block">Meeting Description</label>
                <div className="orka-surface-raised rounded-lg border orka-border-subtle p-3 prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:ml-4 [&_ol]:list-decimal [&_ol]:ml-4 [&_li]:my-1" dangerouslySetInnerHTML={{ __html: task.task_description || '<span class="orka-text-tertiary">No description</span>' }} />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div><label className="text-xs font-medium orka-text-info mb-1 flex items-center gap-1"><User className="w-3 h-3" /> Organizer</label><div className="h-10 px-3 flex items-center orka-surface-raised rounded-md border orka-border-subtle text-sm">{task.meeting_organizer || 'Not specified'}</div></div>
                <div><label className="text-xs font-medium orka-text-info mb-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> Location</label><Input value={localMeetingLocation} onChange={(e) => { setLocalMeetingLocation(e.target.value); handleInteriorChange('meeting_location', e.target.value); }} placeholder="Location..." className="orka-surface-raised orka-border-subtle" /></div>
              </div>

              {attachments.length > 0 && (
                <div><label className="text-xs font-medium orka-text-info mb-2 flex items-center gap-1"><Paperclip className="w-3 h-3" /> Attachments ({attachments.length})</label>
                  <div className="space-y-1">{attachments.map((f, i) => <a key={i} href={f.fileUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-2 orka-surface-raised rounded-lg border orka-border-subtle hover:orka-bg-info-soft dark:hover:bg-blue-900/30"><Paperclip className="w-4 h-4 orka-text-info" /><span className="text-sm orka-text-info truncate">{f.title || f.fileUrl}</span><ExternalLink className="w-3 h-3 text-blue-400 ml-auto" /></a>)}</div>
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-medium orka-text-info flex items-center gap-1"><Users className="w-3 h-3" /> Attendees ({attendees.length})</label>
                  {attendees.length > 0 && <div className="flex items-center gap-3 text-xs">
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full orka-bg-success" />{attendees.filter(a => a.response_status === 'accepted').length} Yes</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full orka-bg-warning" />{attendees.filter(a => a.response_status === 'tentative').length} Maybe</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full orka-bg-danger" />{attendees.filter(a => a.response_status === 'declined').length} No</span>
                  </div>}
                </div>
                <div className="flex gap-2 mb-3">
                  <Input value={newInviteEmail} onChange={(e) => setNewInviteEmail(e.target.value)} placeholder="Enter email to invite..." className="orka-surface-raised orka-border-subtle flex-1" onKeyDown={(e) => e.key === 'Enter' && sendInvite()} />
                  <Button onClick={sendInvite} disabled={inviting || !newInviteEmail} className="orka-bg-info hover:bg-blue-700">{inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}</Button>
                </div>
                {attendees.length > 0 ? (
                  <div className="orka-surface-raised rounded-lg border orka-border-subtle divide-y divide-blue-100 dark:divide-blue-800 max-h-[200px] overflow-y-auto">
                    {attendees.map((a, i) => (
                      <div key={i} className="flex items-center justify-between p-3 hover:orka-bg-info-soft/50 dark:hover:bg-blue-900/20">
                        <div className="flex items-center gap-3">
                          <div className={`w-2.5 h-2.5 rounded-full ${a.response_status === 'accepted' ? 'bg-emerald-500' : a.response_status === 'tentative' ? 'bg-amber-500' : a.response_status === 'declined' ? 'bg-red-500' : 'bg-stone-400'}`} />
                          <div><p className="text-sm font-medium orka-text-primary">{a.name}</p><p className="text-xs orka-text-tertiary">{a.email}</p></div>
                          {a.organizer && <Badge className="orka-bg-info-soft orka-text-info text-[10px]">Organizer</Badge>}
                        </div>
                        <Badge className={getResponseColor(a.response_status)}>{getResponseLabel(a.response_status)}</Badge>
                      </div>
                    ))}
                  </div>
                ) : <div className="text-center py-4 orka-text-tertiary text-sm orka-surface-raised rounded-lg border orka-border-subtle">No attendees yet</div>}
              </div>

              {task.calendar_event_id && <p className="text-xs text-blue-400 dark:text-blue-500 font-mono pt-2 border-t orka-border-subtle">Event ID: {task.calendar_event_id}</p>}
            </div>
          )}

          {/* Delete Button */}
          <div className="pt-3 border-t border orka-border-subtle">
            <Button variant="ghost" className="w-full orka-bg-danger-soft orka-text-danger hover:orka-bg-danger-soft" onClick={() => onDelete?.(task.task_id)}><Trash2 className="w-4 h-4 mr-2" />Delete Task</Button>
          </div>
        </div>
      </div>
      
      {/* Gradient overlay when collapsed to show there's more */}
      {!isVisuallyExpanded && (
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white via-white/90 to-transparent dark:from-slate-900 dark:via-slate-900/90 pointer-events-none" />
      )}
      
      {saving && <div className="absolute inset-0 bg-white/80 /80 flex items-center justify-center z-30"><Loader2 className="w-4 h-4 animate-spin text-blue-500 mr-2" /><span className="text-xs">Saving...</span></div>}
    </div>
  );
};

export default TaskCard;
export { STATUS_META, getTextColorForBg };
export { PRIORITY_META, EFFORT_OPTIONS };
